import { z } from "zod";

/**
 * A single comment annotation attached to a chat.
 * Sub-type of the Chat.comments Json[] field.
 *
 * This is the strict shape Nexus writes today: every field populated.
 * Read paths that need to tolerate historical drift should use
 * `LegacyCommentReadSchema` below.
 */
export const CommentSchema = z.object({
  /** Stable per-comment identifier minted at write time (older rows may omit it) */
  id: z.string().optional(),
  /** ID of the user who left the comment */
  userId: z.string(),
  /** Display name of the user, denormalized at write time so reads don't require a join */
  userName: z.string().optional(),
  /** Comment text content */
  content: z.string(),
  /** ISO date string when the comment was written */
  date: z.string()
});

export type Comment = z.infer<typeof CommentSchema>;

/**
 * Lenient read-side variant of CommentSchema.
 *
 * Pre-existing rows on Chat.comments may have shape drift — historically
 * `userId` was sometimes omitted by older write paths. The conversations
 * read mapper uses this schema with safeParse so legacy rows surface
 * with `userId: null` instead of being silently dropped.
 *
 * Date validity (NaN guard against `new Date(...)` failure) is enforced
 * downstream of the parse — `z.string().datetime()` is too strict for
 * the historical drift this schema deliberately tolerates.
 */
export const LegacyCommentReadSchema = z.object({
  userId: z.string().nullable().optional(),
  content: z.string(),
  date: z.string()
});

export type LegacyCommentRead = z.infer<typeof LegacyCommentReadSchema>;
